import { useEffect, useState } from "react"

import {
  AssessmentHeadline,
  DetectionBoard,
  FileAssessment,
  type OpenEvidence,
  ScoreBreakdownView,
  StoredAssessment,
} from "@/admin/monitoring/assessment"
import type { CompanyRunResearch } from "@/admin/monitoring/company-run-research-data"
import {
  type CompilationAssessment,
  loadCompilationAssessment,
} from "@/admin/monitoring/compilation-assessment-data"
import { AssessmentSummary } from "@/admin/monitoring/compilation-status"
import { AssessmentOutcome } from "@/constants/terminal/assessment-outcome"
import { dataByUnclearReason } from "@/constants/terminal/unclear-reason"
import { errorMessage } from "@/lib/error-message"

// An assessment-only Run's Assessment keeps its band and score; its Score
// Breakdown and Detections are rescored from its frozen inputs, since it
// wrote no Detection revisions. The code can change after the Run, so the
// rescore can differ from what the Assessment stored.
const RescoredAssessment = ({
  companyRunId,
  compilation,
  onOpenEvidence,
}: {
  companyRunId: string
  compilation: NonNullable<CompanyRunResearch["parts"]["assessment"]>
  onOpenEvidence: OpenEvidence
}) => {
  const [rescored, setRescored] = useState<CompilationAssessment | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let isCancelled = false
    setRescored(null)
    setError(null)
    void loadCompilationAssessment({ data: { companyRunId } })
      .then((loaded) => !isCancelled && setRescored(loaded))
      .catch(
        (cause: unknown) =>
          !isCancelled && setError(errorMessage(cause, "Rescoring failed"))
      )
    return () => {
      isCancelled = true
    }
  }, [companyRunId])
  const hasPolicyMoved =
    rescored !== null &&
    (rescored.score !== compilation.score ||
      rescored.band !== compilation.band ||
      rescored.unclearReason !== compilation.unclearReason)
  return (
    <div className="space-y-12">
      <div className="space-y-3">
        <AssessmentHeadline
          band={compilation.band}
          score={compilation.score}
          unclearReason={compilation.unclearReason}
        />
        {hasPolicyMoved && (
          <div className="space-y-1">
            <p>
              Under the current Terminal Policy:{" "}
              <StoredAssessment band={rescored.band} score={rescored.score} />{" "}
              {rescored.unclearReason &&
                dataByUnclearReason[rescored.unclearReason].label}
            </p>
            <p className="text-muted-foreground text-xs">
              The Terminal Policy changed since this Assessment scored, so the
              breakdown below is under the current policy.
            </p>
          </div>
        )}
      </div>
      {error && (
        <p role="alert" className="text-assessment-critical">
          {error}
        </p>
      )}
      {!rescored && !error && (
        <p className="text-muted-foreground">Rescoring the frozen inputs…</p>
      )}
      {rescored && (
        <>
          <ScoreBreakdownView breakdown={rescored.scoreBreakdown} />
          <DetectionBoard
            note="Rescored from the frozen inputs, in the rules’ own words; this Run wrote no Detection rows to open."
            detections={rescored.detections.map((detection) => ({
              key: detection.identity,
              title: detection.presentation.title,
              summary: detection.presentation.summary,
              class: detection.class,
              lifecycleState: detection.lifecycleState,
              typeLabel: detection.typeSnapshot.label,
              details: detection,
              detectionId: null,
            }))}
            onOpenEvidence={onOpenEvidence}
          />
        </>
      )}
    </div>
  )
}

export const AssessmentTab = ({
  research,
  onOpenEvidence,
}: {
  research: CompanyRunResearch
  onOpenEvidence: OpenEvidence
}) => {
  const { parts } = research
  const { assessment } = parts
  const isAssessed = assessment?.outcome === AssessmentOutcome.ASSESSED
  return (
    <div className="space-y-8 text-sm">
      {assessment && parts.isLive && (
        <FileAssessment
          parts={{ ...parts, assessment }}
          onOpenEvidence={onOpenEvidence}
        />
      )}
      {assessment && isAssessed && (
        <RescoredAssessment
          companyRunId={research.companyRunId}
          compilation={assessment}
          onOpenEvidence={onOpenEvidence}
        />
      )}
      {!parts.isLive && !isAssessed && (
        <AssessmentSummary
          assessment={assessment}
          onOpenEvidence={onOpenEvidence}
        />
      )}
    </div>
  )
}
